import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, OnInit, inject } from '@angular/core';
import { NICmsContentConfig } from '@icms/core/content/interfaces/icms-content-config.interfaces';
import { NICmsContentPageType } from '@icms/core/content/interfaces/icms-content-page-type.interfaces';
import mapValues from 'lodash-es/mapValues';
import omit from 'lodash-es/omit';
import { NICmsContentComponent, NICmsContentField } from '../../core/content';
import { ICmsContentService } from '../../core/content/icms-content.service';

interface ICmsMetaData {
    components: NICmsContentComponent.IMeta[];
    pageTypes: { [id: string]: NICmsContentPageType.IMeta };
    configs: NICmsContentConfig.IMeta<any>[];
    timestamp: string;
    version: string;
}

type IFieldSanitized = Omit<
    NICmsContentField.IField,
    'default' | 'helper' | 'canShow' | 'sanitize' | 'prepare' | 'onChange' | 'onFocus'
> & { typeName: string };
type IFieldWithFieldsSanitized = IFieldSanitized & { fields: IFieldSanitized[] };

@Component({
    selector: 'icms-meta',
    standalone: true,
    template: ``,
    styles: [
        `
            :host {
                display: block;
                font-family: monospace;
                white-space: pre-wrap;
                padding: 0;
                margin: 0;
            }
        `
    ]
})
export class ICmsMetaComponent implements OnInit {
    private icmsContentService = inject(ICmsContentService);
    private document = inject(DOCUMENT);

    private elementRef = inject(ElementRef);

    ngOnInit(): void {
        // Set content type header for JSON response
        if (typeof window !== 'undefined' && this.document.defaultView) {
            // Client-side: set meta tag for content type
            const metaTag = this.document.createElement('meta');
            metaTag.httpEquiv = 'Content-Type';
            metaTag.content = 'application/json';
            this.document.head.appendChild(metaTag);
        }

        // Collect all ICMS metadata
        const metaJson = JSON.stringify({
            components: this.icmsContentService.getComponentMetas().map(i => ({
                ...i,
                fields: i.fields.map(f => this.sanitizeField(f))
            })),
            pageTypes: mapValues(ICmsContentService.pageTypes || {}, i => ({
                ...i,
                fields: i.fields?.map(f => this.sanitizeField(f)) || []
            })),
            configs: this.icmsContentService.getConfigs().map(i => ({
                ...i,
                fields: i.fields.map(f => this.sanitizeField(f))
            })),
            timestamp: new Date().toISOString(),
            version: '1.0.0'
        });

        this.elementRef.nativeElement.innerText = btoa(metaJson);
    }

    private sanitizeField(field: NICmsContentField.IField): IFieldWithFieldsSanitized | IFieldSanitized {
        const ignoredFields = ['default', 'helper', 'canShow', 'sanitize', 'prepare', 'onChange', 'onFocus'];
        const fieldSanitized = omit(field, ...ignoredFields) as IFieldSanitized;
        if ('fields' in field) {
            const fields = (field as NICmsContentField.IRepeaterField).fields.map(f => this.sanitizeField(f));
            return {
                ...fieldSanitized,
                typeName: this.getFieldTypeMapping()[field.type],
                fields: fields
            };
        }
        return {
            ...fieldSanitized,
            typeName: this.getFieldTypeMapping()[field.type]
        };
    }

    private getFieldTypeMapping(): { [key: number]: string } {
        const fieldTypeMapping: { [key: number]: string } = {};

        // Map enum values to their string names
        Object.keys(NICmsContentField.EFieldType).forEach(key => {
            const enumValue = NICmsContentField.EFieldType[key as keyof typeof NICmsContentField.EFieldType];
            if (typeof enumValue === 'number') {
                fieldTypeMapping[enumValue] = key;
            }
        });

        return fieldTypeMapping;
    }
}
